import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { getCaptchaEmailFormsEnabled, getCaptchaEnabled, getCaptchaSiteKey } from "@/mk2/reducers";

/**
 * Cloudflare Turnstile widget. Renders only when the `captcha_enabled` runtime flag
 * is on (seeded server-side into the store), yields a one-time token via `onToken`,
 * and clears it on expiry/error so the caller never submits a stale token. SSR-safe:
 * the script + widget are created client-side only.
 */

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

interface TurnstileApi {
    render: (
        el: HTMLElement,
        opts: {
            sitekey: string;
            callback: (token: string) => void;
            "expired-callback"?: () => void;
            "error-callback"?: () => void;
        },
    ) => string;
    reset: (widgetId?: string) => void;
    remove: (widgetId: string) => void;
}

declare global {
    interface Window {
        turnstile?: TurnstileApi;
    }
}

let scriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
    if (typeof window === "undefined") {
        return Promise.reject(new Error("no window"));
    }
    if (window.turnstile) {
        return Promise.resolve();
    }
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = TURNSTILE_SRC;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error("turnstile script failed"));
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
}

interface Props {
    onToken: (token: string) => void;
    // Which runtime flag gates this widget: the signup `captcha_enabled` flag
    // (default) or `captcha_email_forms` for the anonymous email forms.
    flag?: "signup" | "emailForms";
}

export const CaptchaWidget: React.FC<Props> = ({ onToken, flag = "signup" }) => {
    const signupEnabled = useSelector(getCaptchaEnabled);
    const emailFormsEnabled = useSelector(getCaptchaEmailFormsEnabled);
    const enabled = flag === "emailForms" ? emailFormsEnabled : signupEnabled;
    const siteKey = useSelector(getCaptchaSiteKey);
    const containerRef = useRef<HTMLDivElement>(null);
    const widgetIdRef = useRef<string | null>(null);
    // Keep the latest callback in a ref so re-renders don't re-init the widget.
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useEffect(() => {
        if (!enabled || !siteKey || typeof window === "undefined") {
            return undefined;
        }
        let cancelled = false;
        loadTurnstileScript()
            .then(() => {
                if (cancelled || !containerRef.current || !window.turnstile) {
                    return;
                }
                widgetIdRef.current = window.turnstile.render(containerRef.current, {
                    sitekey: siteKey,
                    callback: (token: string) => onTokenRef.current(token),
                    "expired-callback": () => onTokenRef.current(""),
                    "error-callback": () => onTokenRef.current(""),
                });
            })
            .catch(() => {
                /* script/network failure — backend fails closed, nothing to render */
            });
        return () => {
            cancelled = true;
            if (widgetIdRef.current && window.turnstile) {
                try {
                    window.turnstile.remove(widgetIdRef.current);
                } catch (e) {
                    /* widget already gone */
                }
                widgetIdRef.current = null;
            }
        };
    }, [enabled, siteKey]);

    if (!enabled || !siteKey) {
        return null;
    }
    return (
        <div
            ref={containerRef}
            style={{ display: "flex", justifyContent: "center", margin: "12px 0" }}
        />
    );
};
